// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { History, ChevronDown, ChevronRight } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, Badge, Skeleton, Select, Pagination, EmptyState, Input } from '@/components/ui'
import { useActivityLogs } from '@/hooks/useActivityLogs'
import { useUsers } from '@/hooks/useUsers'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'
import type { AuditLog } from '@/types'

// The value audited_redact.rb writes for a C3 value, and for every value of
// an erased customer (EP-159 E2, EP-160 section 2.7).
const REDACTED_VALUE = '[REDACTED]'

const AUDITABLE_TYPES = [
  'Property', 'Customer', 'Unit', 'Lease', 'Invoice', 'Payment',
  'MaintenanceRequest', 'Parcel', 'RoomType', 'Meter', 'Reading', 'DocumentTemplate',
]

const ACTIONS = ['create', 'update', 'destroy']

export function ActivityLogPage() {
  const { t, i18n } = useTranslation('settings')
  const formatDateTime = useDateTimeFormatter()
  const [page, setPage] = useState(1)
  const [typeFilter, setTypeFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const { data, isLoading, isFetching } = useActivityLogs(
    page,
    typeFilter || undefined,
    actionFilter || undefined,
    userFilter ? Number(userFilter) : undefined,
    dateFrom || undefined,
    dateTo || undefined
  )

  const { data: usersData } = useUsers()
  const users = usersData?.data || []

  const audits = data?.data || []

  const typeOptions = [
    { value: '', label: t('activity_log.all_types') },
    ...AUDITABLE_TYPES.map((type) => ({
      value: type,
      label: t(`activity_log.model_types.${type}`),
    })),
  ]

  const actionOptions = [
    { value: '', label: t('activity_log.all_actions') },
    ...ACTIONS.map((action) => ({
      value: action,
      label: t(`activity_log.actions.${action}`),
    })),
  ]

  const userOptions = [
    { value: '', label: t('activity_log.all_users') },
    ...users.map((u) => ({
      value: String(u.id),
      label: `${u.first_name} ${u.last_name}`.trim() || u.email,
    })),
  ]

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'create':
        return <Badge variant="success">{t('activity_log.actions.create')}</Badge>
      case 'update':
        return <Badge variant="info">{t('activity_log.actions.update')}</Badge>
      case 'destroy':
        return <Badge variant="error">{t('activity_log.actions.destroy')}</Badge>
      default:
        return <Badge variant="ghost">{action}</Badge>
    }
  }

  // audit.auditable_type spans every `audited` backend model (40+), far more
  // than the fixed filter-dropdown list above — most have no locale entry, so
  // a blind t() would render the raw key. Branch on i18n.exists(): known
  // models translate; unknown models fall back to a humanized PascalCase split.
  const formatModelType = (type: string) => {
    const key = `activity_log.model_types.${type}`
    return i18n.exists(`settings:${key}`) ? t(key) : type.replace(/([A-Z])/g, ' $1').trim()
  }

  // "[REDACTED]" reads "[ลบแล้ว]"; anything else is shown as stored.
  const formatValue = (value: unknown) =>
    value === REDACTED_VALUE ? t('activity_log.removed_value') : JSON.stringify(value)

  // EP-160 S4: "ทีม FlowRent (Nok) · เซสชัน #12" for the FlowRent team, else
  // the user's name. Never an email.
  const actorLabel = (user: NonNullable<AuditLog['user']>) => {
    if (!('actor' in user)) return user.name
    return user.session_id
      ? t('activity_log.support_actor', { name: user.name, id: user.session_id })
      : t('activity_log.support_actor_no_session', { name: user.name })
  }

  const renderChanges = (audit: AuditLog) => {
    const changes = audit.audited_changes
    if (!changes || Object.keys(changes).length === 0) return null

    if (audit.action === 'create') {
      return (
        <div className="mt-2 bg-base-200 p-3 rounded-lg text-sm">
          <div className="grid gap-1">
            {Object.entries(changes).map(([key, value]) => (
              <div key={key} className="flex gap-2">
                <span className="text-base-content/60 min-w-[120px]">{key}:</span>
                <span className="text-success">{formatValue(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )
    }

    if (audit.action === 'update') {
      return (
        <div className="mt-2 bg-base-200 p-3 rounded-lg text-sm">
          <div className="grid gap-1">
            {Object.entries(changes).map(([key, value]) => {
              const [oldVal, newVal] = Array.isArray(value) ? value : [null, value]
              return (
                <div key={key} className="flex gap-2">
                  <span className="text-base-content/60 min-w-[120px]">{key}:</span>
                  {oldVal !== null && (
                    <>
                      <span className="text-error line-through">{formatValue(oldVal)}</span>
                      <span className="text-base-content/40">&rarr;</span>
                    </>
                  )}
                  <span className="text-success">{formatValue(newVal)}</span>
                </div>
              )
            })}
          </div>
        </div>
      )
    }

    return (
      <div className="mt-2 bg-base-200 p-3 rounded-lg text-sm text-base-content/60">
        {t('activity_log.record_deleted')}
      </div>
    )
  }

  return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title'), href: '/settings' }, { label: t('activity_log.title') }]} />
        <PageHeader
          title={t('activity_log.title')}
          description={t('activity_log.description')}
          isRefreshing={isFetching && !isLoading}
        />

        {/* Filters */}
        <Card>
          <div className="space-y-3">
            <Select
              label={t('activity_log.type_label')}
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
              options={typeOptions}
              className="w-full"
            />
            <div className="grid grid-cols-2 gap-4">
              <Select
                label={t('activity_log.action_label')}
                value={actionFilter}
                onChange={(e) => { setActionFilter(e.target.value); setPage(1) }}
                options={actionOptions}
              />
              <Select
                label={t('activity_log.user_label')}
                value={userFilter}
                onChange={(e) => { setUserFilter(e.target.value); setPage(1) }}
                options={userOptions}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('activity_log.date_from')}
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1) }}
              />
              <Input
                label={t('activity_log.date_to')}
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1) }}
              />
            </div>
          </div>
        </Card>

        {/* Content */}
        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}
            </div>
          </Card>
        ) : audits.length === 0 ? (
          <Card>
            <EmptyState
              icon={<History className="w-12 h-12" />}
              title={t('activity_log.empty_title')}
              description={t('activity_log.empty_description')}
            />
          </Card>
        ) : (
          <Card>
            <div className="divide-y divide-base-200">
              {audits.map((audit) => {
                const isExpanded = expandedId === audit.id
                return (
                  <div key={audit.id} className="py-3">
                    <div
                      className="flex items-center gap-3 cursor-pointer hover:bg-base-100 -mx-2 px-2 py-1 rounded"
                      onClick={() => setExpandedId(isExpanded ? null : audit.id)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-base-content/40 shrink-0" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-base-content/40 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {getActionBadge(audit.action)}
                          <span className="font-medium">{formatModelType(audit.auditable_type)}</span>
                          <span className="text-base-content/40">#{audit.auditable_id}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {audit.user && (
                          <span className="text-sm text-base-content/60" data-testid={`audit-actor-${audit.id}`}>
                            {actorLabel(audit.user)}
                          </span>
                        )}
                        <span className="text-sm text-base-content/40">
                          {formatDateTime(audit.created_at)}
                        </span>
                      </div>
                    </div>
                    {isExpanded && renderChanges(audit)}
                  </div>
                )
              })}
            </div>
          </Card>
        )}

        {/* Pagination */}
        {data?.meta && data.meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
  )
}
